<template>
  <div>
    <template v-if="loading">
      <div class="tw-p-4"><WdsSpinner indeterminate :size="40" /></div>
    </template>
    <FreelancerBusinessEntityAlert
      v-if="freelancerBusinessEntityCompliance !== null"
      :market="freelancerBusinessEntityCompliance.data.market"
    />

    <AcceptDeterminationAlert
      v-if="determinationCompliance !== null"
      :compliance-data="determinationCompliance.data"
    />

    <template v-for="compliance in compliancesHire">
      <AddressIncompleteAlert
        v-if="compliance.name === ComplianceName.ADDRESS"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />

      <BankAccountAlert
        v-if="compliance.name === ComplianceName.BANK_ACCOUNT"
        :key="compliance.name"
      />

      <PendingContractChangesAlert
        v-if="compliance.name === ComplianceName.CONTRACT_CHANGES"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />

      <AcceptContractDocsAlert
        v-if="compliance.name === ComplianceName.CONTRACT_DOC"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />

      <FullNameIncompleteAlert
        v-if="compliance.name === ComplianceName.FULL_NAME"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />

      <UkTaxAlert
        v-if="compliance.name === ComplianceName.UK_TAX"
        :key="compliance.name"
        :compliance-data="compliance.data"
      />

      <UsTaxAlert
        v-if="compliance.name === ComplianceName.US_TAX"
        :key="compliance.name"
        :compliance-data="compliance.data"
      />

      <PayeAcceptAlert
        v-if="compliance.name === ComplianceName.PAYE_ACCEPT"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />

      <PayeContractDatesRequiredAlert
        v-if="compliance.name === ComplianceName.UK_PAYE_REQUIRED_CONTRACT_DATES"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />

      <PayeSettingsRequiredAlert
        v-if="compliance.name === ComplianceName.UK_PAYROLL"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />
      <InvalidVatAlert
        v-if="compliance.name === ComplianceName.VALID_VAT"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />
    </template>
  </div>
</template>

<script setup>
import { useQuery } from '@vue/apollo-composable'
import { WdsSpinner } from '@worksome/wds-components'
import gql from 'graphql-tag'
import { computed, defineAsyncComponent, defineProps } from 'vue'
import { fragments as AcceptContractDocsAlertFragments } from '@/components/compliances/alerts/AcceptContractDocsAlert.vue'
import {
  ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT,
  ACTIVE_COMPLIANCES_ON_HIRE_FRAGMENT,
} from '@/components/compliances/queries/Compliances'
import {
  ComplianceName,
  determinationCompliances,
  freelancerBusinessEntityCompliances,
} from '@/components/compliances/types/ComplianceName.enum'
import { ParseJSONPropertyInObject } from '@/utils/ParseJSONPropertyInObject'

const AcceptContractDocsAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/AcceptContractDocsAlert.vue'),
)
const AcceptDeterminationAlert = defineAsyncComponent(
  () => import('@/components/compliances/Determinations/alerts/AcceptDeterminationAlert.vue'),
)
const AddressIncompleteAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/AddressIncompleteAlert.vue'),
)
const BankAccountAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/BankAccountAlert.vue'),
)
const FullNameIncompleteAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/FullNameIncompleteAlert.vue'),
)
const PayeAcceptAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/PayeAcceptAlert.vue'),
)
const PayeContractDatesRequiredAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/PayeContractDatesRequiredAlert.vue'),
)
const PayeSettingsRequiredAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/PayeSettingsRequiredAlert.vue'),
)
const PendingContractChangesAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/PendingContractChangesAlert.vue'),
)
const FreelancerBusinessEntityAlert = defineAsyncComponent(
  () => import('@/components/compliances/BusinessEntity/alerts/FreelancerBusinessEntityAlert.vue'),
)
const UsTaxAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/UsTaxAlert.vue'),
)
const UkTaxAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/UkTaxAlert.vue'),
)
const UkValidContractSetupAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/UkValidContractSetupAlert.vue'),
)

const InvalidVatAlert = defineAsyncComponent(
  () => import('@/components/compliances/alerts/InvalidVatAlert.vue'),
)

const props = defineProps({
  hireId: {
    type: Number,
    required: true,
  },
})

const { result: resultHire, loading: loadingHire } = useQuery(
  gql`
    query getHire($hireId: Int!, $complianceNames: [ComplianceName!]!) {
      hire(localId: $hireId) {
        threadId
        ...ActiveCompliancesOnHire
        ...AcceptContractDocsAlertHire
      }
    }
    ${ACTIVE_COMPLIANCES_ON_HIRE_FRAGMENT}
    ${AcceptContractDocsAlertFragments.hire}
  `,
  {
    hireId: props.hireId,
    complianceNames: [
      ...determinationCompliances(),
      ComplianceName.ADDRESS,
      ComplianceName.BANK_ACCOUNT,
      ComplianceName.CONTRACT_CHANGES,
      ComplianceName.CONTRACT_DOC,
      ComplianceName.FULL_NAME,
      ComplianceName.PAYE_ACCEPT,
      ComplianceName.UK_PAYE_REQUIRED_CONTRACT_DATES,
      ComplianceName.UK_PAYROLL,
      ComplianceName.UK_TAX,
      ComplianceName.US_TAX,
      ComplianceName.VALID_VAT,
    ],
  },
)
const hire = computed(() => resultHire?.value?.hire)
const compliancesHire = computed(
  () =>
    hire.value?.compliances
      ?.filter((compliance) => compliance.isApplicable && !compliance.isComplete)
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)
const determinationCompliance = computed(
  () =>
    compliancesHire.value.find((compliance) =>
      determinationCompliances().includes(compliance.name),
    ) ?? null,
)

const { result: resultAccount, loading: loadingAccount } = useQuery(
  gql`
    query accountQuery($complianceNames: [ComplianceName!]!) {
      me {
        activeAccount {
          __typename
          ...ActiveCompliancesOnAccount
        }
      }
    }
    ${ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT}
  `,
  {
    complianceNames: freelancerBusinessEntityCompliances(),
  },
)
const activeAccount = computed(() => resultAccount?.value?.me?.activeAccount)
const compliancesAccount = computed(
  () =>
    activeAccount.value?.compliances
      ?.filter((compliance) => compliance.isApplicable && !compliance.isComplete)
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)
const freelancerBusinessEntityCompliance = computed(
  () =>
    compliancesAccount.value.find((compliance) =>
      freelancerBusinessEntityCompliances().includes(compliance.name),
    ) ?? null,
)

const loading = computed(() => loadingHire.value || loadingAccount.value)
</script>
